import type { CrisisManagementFrameworkResult } from '../types.js';
import { S13_INCIDENT_TYPES, S13_PRIORITIES, type S13Intake } from './s13Workflow.js';

const text = (value: unknown, maximum = 16000) => String(value ?? '').trim().slice(0, maximum);
const comparable = (value: unknown) => text(value).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

export function s13ScenarioTrigger(trigger: string, incidentType: string, priority: string,
    rules: Array<{ priority: string; trigger: string }>,
    matrix: Array<{ incidentType: string; levels: Array<{ priority: string; escalationTrigger: string }> }>): string {
    const value = comparable(trigger);
    const rule = rules.find(item => item.priority === priority);
    if (!value || value === comparable(rule?.trigger)
        || matrix.some(row => row.levels.some(cell =>
            (row.incidentType !== incidentType || cell.priority !== priority)
            && comparable(cell.escalationTrigger) === value)))
        return `Needs verification: define a ${priority} trigger for ${incidentType}.`;
    return trigger;
}

export function s13MatrixGaps(report: CrisisManagementFrameworkResult): Array<{ incidentType: string; priority: string }> {
    const gaps: Array<{ incidentType: string; priority: string }> = [];
    for (const incidentType of S13_INCIDENT_TYPES) {
        const row = report.incidentClassificationMatrix.find(item => item.incidentType === incidentType);
        for (const priority of S13_PRIORITIES) {
            const cell = row?.levels.find(item => item.priority === priority);
            const trigger = s13ScenarioTrigger(cell?.escalationTrigger || '', incidentType, priority,
                report.priorityRules, report.incidentClassificationMatrix);
            if (/^needs verification\b/i.test(trigger))
                gaps.push({ incidentType, priority });
        }
    }
    return gaps;
}

export function cleanS13Report(source: any, intake: S13Intake): CrisisManagementFrameworkResult {
    const raw = source && typeof source === 'object' ? source : {};
    const matrix: any[] = Array.isArray(raw.incidentClassificationMatrix) ? raw.incidentClassificationMatrix : [];
    const cards: any[] = Array.isArray(raw.responseCards) ? raw.responseCards : [];
    const incidentClassificationMatrix = S13_INCIDENT_TYPES.map(incidentType => {
        const row = matrix.find(item => text(item?.incidentType, 160).toLowerCase() === incidentType.toLowerCase()) || {};
        return { incidentType, levels: S13_PRIORITIES.map(priority => {
            const rule = intake.priorityRules.find(item => item.priority === priority)!;
            const cell = (Array.isArray(row.levels) ? row.levels : []).find((item: any) => item?.priority === priority) || {};
            return { priority, responseMinutes: Number(rule.responseMinutes), commandAuthority: rule.authority,
                escalationTrigger: text(cell.escalationTrigger, 1000),
                notificationRequirements: text(cell.notificationRequirements, 1000) || 'Consultant verification required.' };
        }) };
    });
    return {
        reportType: 'CrisisManagementFrameworkResult', workflowVersion: 'CMP-13-v2',
        organizationName: intake.organizationName, industry: intake.industry, sites: intake.sites,
        siteDetails: intake.siteDetails,
        commandTeam: intake.commandTeam, reviewFrequency: intake.reviewFrequency,
        criticalFunctions: intake.criticalFunctions.map(item => ({ name: item.name, rtoHours: Number(item.rtoHours) })),
        priorityRules: intake.priorityRules.map(item => ({ priority: item.priority, trigger: item.trigger,
            responseMinutes: Number(item.responseMinutes), authority: item.authority })),
        externalSpokesperson: intake.externalSpokesperson,
        insuranceNotifications: intake.insuranceNotifications, supplierContacts: intake.supplierContacts,
        policeFireContacts: intake.policeFireContacts || '',
        debriefProtocol: intake.debriefProtocol,
        executiveSummary: text(raw.executiveSummary), siteProfiles: text(raw.siteProfiles),
        incidentClassificationMatrix: incidentClassificationMatrix.map(row => ({ ...row, levels: row.levels.map(cell => ({
            ...cell, escalationTrigger: s13ScenarioTrigger(cell.escalationTrigger, row.incidentType,
                cell.priority, intake.priorityRules, incidentClassificationMatrix),
        })) })),
        responseCards: S13_INCIDENT_TYPES.map(incidentType => {
            const card = cards.find(item => text(item?.incidentType, 160).toLowerCase() === incidentType.toLowerCase()) || {};
            return { incidentType, immediateActions: text(card.immediateActions, 4000),
                escalation: text(card.escalation, 2000), transition: text(card.transition, 2000) };
        }),
        contactDirectory: text(raw.contactDirectory), communications: text(raw.communications),
        recovery: text(raw.recovery), legalInsurance: text(raw.legalInsurance), postIncident: text(raw.postIncident),
        evidenceGaps: [...new Set((Array.isArray(raw.evidenceGaps) ? raw.evidenceGaps : []).slice(0, 25)
            .map((item: unknown): string => text(item, 500)).filter(Boolean))] as string[],
        timestamp: text(raw.timestamp, 50) || new Date().toISOString(),
    };
}

export function missingS13Report(report: CrisisManagementFrameworkResult, matrixAcknowledged = false): string[] {
    const sections = [
        ['Executive Summary', report.executiveSummary], ['Site Profiles', report.siteProfiles],
        ['Contact Directory', report.contactDirectory], ['Communications', report.communications],
        ['Recovery', report.recovery], ['Legal and Insurance', report.legalInsurance],
        ['Post-Incident Review', report.postIncident],
    ];
    const missing = sections.filter(([, value]) => !text(value)).map(([name]) => name);
    if (report.incidentClassificationMatrix.length !== S13_INCIDENT_TYPES.length
        || report.incidentClassificationMatrix.some(row => row.levels.length !== S13_PRIORITIES.length
            || row.levels.some(cell => !cell.notificationRequirements))
        || (!matrixAcknowledged && s13MatrixGaps(report).length))
        missing.push('Eight-by-four incident classification matrix');
    if (report.responseCards.length !== S13_INCIDENT_TYPES.length
        || report.responseCards.some(card => !card.immediateActions || !card.escalation || !card.transition))
        missing.push('Eight incident response cards');
    return missing;
}

export function s13ReviewError(previous: string, next: string, report: CrisisManagementFrameworkResult,
    changed: boolean, matrixAcknowledged = false): string | null {
    if (next === 'Approved' && previous !== 'Validated')
        return 'Validate the S-13 plan before approval and sign-off.';
    const missing = missingS13Report(report, matrixAcknowledged);
    if (next !== 'Draft' && missing.length)
        return `Complete the report: ${missing.slice(0, 3).join(', ')}.`;
    if (next === 'Approved' && changed)
        return 'Save and validate report changes before approval.';
    return null;
}

export function s13DeliveryLink(requestId: string, reference: string) {
    const id = `service-${requestId}-s13`;
    return { id, sourceId: id, title: `${reference} Crisis Management Plan`,
        type: 'CrisisManagementFrameworkResult' as const };
}
